/**
 * GrantModal Component
 *
 * Modal for creating and editing grants matching the Grant schema (Permission System v5.002).
 * Replaces the legacy GrantForm tab with a modal-based approach consistent with other admin tasks.
 *
 * v5.002 Schema Fields:
 * - name, description: Human-readable context
 * - subjectType, subjectValue: Who the grant applies to (ROLE/USER)
 * - targetDomain, targetResourceTypeId, targetResourceId: Structured resource targeting (v5.002 - no action)
 * - actionConfigs: Array of { action, effect, scopeConfig, priority?, constraints? }
 * - priority: Grant-level default priority (1-999)
 * - effectiveFrom, effectiveTo: Temporal controls
 * - constraints: Grant-level default constraints
 * - isActive: Status boolean
 */

import React, { useState, useEffect } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Radio,
  RadioGroup,
  FormLabel,
  Typography,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Grid,
  Divider,
  IconButton,
  Chip,
  CircularProgress
} from '@mui/material';
import {
  Close as CloseIcon,
  Save as SaveIcon,
  ExpandMore as ExpandMoreIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { API, graphqlOperation } from 'aws-amplify';

import AdminButton from '../../../../../components/common/AdminButton';
import { listResourceTypes, resourceCatalogByType, listUsers } from '../../../../../graphql/queries';

// GroupRole enum values - all roles
const ALL_GROUP_ROLES = [
  { value: 'APP_ADMIN', label: 'APP_ADMIN - RadixDash Application Administration', clientRestriction: 'rdx' },
  { value: 'CLIENT_ADMIN', label: 'CLIENT_ADMIN - Client Organization Administration' },
  { value: 'MANAGER', label: 'MANAGER - Management Authority' },
  { value: 'ASSOCIATE', label: 'ASSOCIATE - Standard Access' }
];

/**
 * Get available roles for a specific client
 * APP_ADMIN is only available for RadixDash (rdx) client
 */
const getAvailableRoles = (clientId) => {
  return ALL_GROUP_ROLES.filter(role =>
    !role.clientRestriction || role.clientRestriction === clientId
  );
};

// Validation schema matching the Grant model (v5.002)
const validationSchema = Yup.object({
  name: Yup.string()
    .required('Name is required')
    .min(3, 'Name must be at least 3 characters')
    .max(100, 'Name must be less than 100 characters'),
  description: Yup.string()
    .required('Description is required')
    .min(10, 'Description must be at least 10 characters')
    .max(500, 'Description must be less than 500 characters'),
  subjectType: Yup.string()
    .oneOf(['USER', 'ROLE'], 'Subject type must be USER or ROLE')
    .required('Subject type is required'),
  subjectValue: Yup.string()
    .required('Subject value is required')
    .min(2, 'Subject value must be at least 2 characters'),
  priority: Yup.number()
    .min(1, 'Priority must be between 1 and 999')
    .max(999, 'Priority must be between 1 and 999')
    .required('Priority is required'),
  // v5.002: actionConfigs array is required with at least one action
  actionConfigs: Yup.array()
    .of(
      Yup.object({
        action: Yup.string()
          .oneOf(['VIEW', 'EDIT', 'CREATE', 'DELETE', 'DEACTIVATE', 'EXPORT'], 'Invalid action')
          .required('Action is required'),
        effect: Yup.string()
          .oneOf(['ALLOW', 'DENY'], 'Effect must be ALLOW or DENY')
          .required('Effect is required'),
        scopeConfig: Yup.object({
          includeChildren: Yup.boolean().required(),
          includePeers: Yup.boolean().required(),
          includeParent: Yup.boolean().required()
        }).required('Scope configuration is required'),
        priority: Yup.number()
          .min(1, 'Priority must be between 1 and 999')
          .max(999, 'Priority must be between 1 and 999')
          .nullable(),
        constraints: Yup.object().nullable()
      })
    )
    .min(1, 'At least one action configuration is required')
    .required('Action configurations are required'),
  // v5.002: Resource targeting fields are optional (null = wildcard)
  targetDomain: Yup.string().nullable(),
  targetResourceTypeId: Yup.string().nullable(),
  targetResourceId: Yup.string().nullable(),
  effectiveFrom: Yup.date().nullable(),
  effectiveTo: Yup.date()
    .nullable()
    .when('effectiveFrom', (effectiveFrom, schema) => {
      return effectiveFrom
        ? schema.min(effectiveFrom, 'Effective To must be after Effective From')
        : schema;
    })
});

export default function GrantModal({
  open,
  onClose,
  grant,
  onSave,
  user,
  selectedClient,
  availableClients = []
}) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [resourceTypes, setResourceTypes] = useState([]);
  const [resources, setResources] = useState([]);
  const [loadingResourceTypes, setLoadingResourceTypes] = useState(false);
  const [loadingResources, setLoadingResources] = useState(false);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const mode = grant ? 'edit' : 'create';
  const isAppAdmin = user?.groupRole === 'APP_ADMIN' || user?.role === 'Admin';
  // Use selectedClient from AdminContext instead of user.client
  const defaultClientId = selectedClient || user?.client || user?.clientId || 'grm';

  // Initial values (v5.002)
  const initialValues = grant ? {
    ...grant,
    // Ensure nulls for wildcards (not undefined)
    targetDomain: grant.targetDomain || null,
    targetResourceTypeId: grant.targetResourceTypeId || null,
    targetResourceId: grant.targetResourceId || null,
    effectiveFrom: grant.effectiveFrom || null,
    effectiveTo: grant.effectiveTo || null,
    constraints: grant.constraints || null,
    // v5.002: Ensure actionConfigs array exists
    actionConfigs: grant.actionConfigs || [{
      action: 'VIEW',
      effect: 'ALLOW',
      scopeConfig: {
        includeChildren: false,
        includePeers: false,
        includeParent: false
      },
      priority: null,
      constraints: null
    }]
  } : {
    clientId: defaultClientId,
    name: '',
    description: '',
    subjectType: 'ROLE',
    subjectValue: '',
    targetDomain: null,
    targetResourceTypeId: null,
    targetResourceId: null,
    priority: 100,
    isActive: true,
    effectiveFrom: null,
    effectiveTo: null,
    constraints: null,
    // v5.002: Default to one VIEW ALLOW action config
    actionConfigs: [{
      action: 'VIEW',
      effect: 'ALLOW',
      scopeConfig: {
        includeChildren: false,
        includePeers: false,
        includeParent: false
      },
      priority: null,
      constraints: null
    }]
  };

  // Load resource types and users on mount
  useEffect(() => {
    if (open) {
      fetchResourceTypes();
      fetchUsers();
      // If editing a grant with a resource type, load its resources
      if (grant?.targetResourceTypeId) {
        fetchResourcesByType(grant.targetResourceTypeId, grant.targetDomain);
      }
    }
  }, [open, grant]);

  const fetchResourceTypes = async () => {
    setLoadingResourceTypes(true);
    try {
      const result = await API.graphql(
        graphqlOperation(listResourceTypes, {
          filter: { status: { eq: 'ACTIVE' } },
          limit: 1000
        })
      );
      setResourceTypes(result.data.listResourceTypes?.items || []);
    } catch (error) {
      console.error('Error fetching resource types:', error);
    } finally {
      setLoadingResourceTypes(false);
    }
  };

  const fetchResourcesByType = async (resourceTypeId, domain) => {
    if (!resourceTypeId) {
      setResources([]);
      return;
    }

    setLoadingResources(true);
    try {
      const result = await API.graphql(
        graphqlOperation(resourceCatalogByType, {
          resourceTypeId,
          status: { eq: 'ACTIVE' },
          filter: domain ? { domain: { eq: domain } } : null,
          limit: 1000
        })
      );
      setResources(result.data.resourceCatalogByType?.items || []);
    } catch (error) {
      console.error('Error fetching resources:', error);
      setResources([]);
    } finally {
      setLoadingResources(false);
    }
  };

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      // Build filter based on user permissions
      let filter = {};

      // If CLIENT_ADMIN, only show users from their client
      if (!isAppAdmin) {
        filter.client = { eq: defaultClientId };
      }
      // If APP_ADMIN, show all users from all clients (no filters)

      const result = await API.graphql(
        graphqlOperation(listUsers, {
          filter: Object.keys(filter).length > 0 ? filter : null,
          limit: 1000
        })
      );
      let usersList = result.data.listUsers?.items || [];
      console.log(`Fetched ${usersList.length} users (isAppAdmin: ${isAppAdmin}, clientId: ${defaultClientId})`);

      // Filter out deleted/suspended users in JavaScript
      // Include users with ACTIVE status or null status (legacy users)
      usersList = usersList.filter(u =>
        !u.status || u.status === 'ACTIVE'
      );
      console.log(`After status filter: ${usersList.length} users`);

      // Sort by client first, then by lastName, firstName
      usersList.sort((a, b) => {
        // First compare by client
        const clientCompare = (a.client || '').localeCompare(b.client || '');
        if (clientCompare !== 0) return clientCompare;

        // Then by lastName, firstName
        const aName = `${a.lastName || ''} ${a.firstName || ''}`.trim();
        const bName = `${b.lastName || ''} ${b.firstName || ''}`.trim();
        return aName.localeCompare(bName);
      });
      setUsers(usersList);
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleSubmit = async (values, { setSubmitting, setErrors }) => {
    setSaving(true);
    setError(null);

    try {
      // v5.002: Clean up the values before submission
      const cleanedValues = {
        ...values,
        // Convert empty strings to null for optional fields
        targetDomain: values.targetDomain || null,
        targetResourceTypeId: values.targetResourceTypeId || null,
        targetResourceId: values.targetResourceId || null,
        effectiveFrom: values.effectiveFrom || null,
        effectiveTo: values.effectiveTo || null,
        constraints: values.constraints || null,
        // v5.002: Ensure actionConfigs are properly formatted
        actionConfigs: values.actionConfigs.map(ac => ({
          action: ac.action,
          effect: ac.effect,
          scopeConfig: ac.scopeConfig,
          priority: ac.priority || null,
          constraints: ac.constraints || null
        }))
      };

      await onSave(cleanedValues);
      onClose();
    } catch (err) {
      console.error('Error saving grant:', err);
      setError(err.message || 'Failed to save grant');
      setErrors({ submit: err.message || 'Failed to save grant' });
    } finally {
      setSaving(false);
      setSubmitting(false);
    }
  };

  const isFullWildcard = (values) => {
    // v5.002: Full wildcard is when no resource targeting is specified
    return !values.targetDomain &&
           !values.targetResourceTypeId &&
           !values.targetResourceId;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {mode === 'edit' ? 'Edit Grant' : 'Create New Grant'}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          setFieldValue,
          isSubmitting
        }) => (
          <Form>
            <DialogContent dividers>
              <Stack spacing={3}>
                {/* Error Alert */}
                {error && (
                  <Alert severity="error" onClose={() => setError(null)}>
                    {error}
                  </Alert>
                )}

                {/* Basic Information */}
                <Box>
                  <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                    Basic Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      {isAppAdmin ? (
                        <FormControl fullWidth size="small" required>
                          <InputLabel>Client</InputLabel>
                          <Select
                            name="clientId"
                            value={values.clientId}
                            onChange={(e) => {
                              handleChange(e);
                              // If changing away from rdx and APP_ADMIN is selected, reset subjectValue
                              if (e.target.value !== 'rdx' && values.subjectType === 'ROLE' && values.subjectValue === 'APP_ADMIN') {
                                setFieldValue('subjectValue', '');
                              }
                            }}
                            label="Client"
                          >
                            {availableClients.map((client) => (
                              <MenuItem key={client.clientId} value={client.clientId}>
                                {client.clientName} ({client.clientId})
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <TextField
                          fullWidth
                          size="small"
                          name="clientId"
                          label="Client"
                          value={values.clientId}
                          disabled
                          helperText="CLIENT_ADMIN users can only create grants for their own client"
                        />
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        name="name"
                        label="Grant Name"
                        value={values.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.name && !!errors.name}
                        helperText={touched.name && errors.name}
                        required
                        placeholder="e.g., View Operations Reports"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        multiline
                        rows={2}
                        name="description"
                        label="Description"
                        value={values.description}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.description && !!errors.description}
                        helperText={touched.description && errors.description}
                        required
                        placeholder="Explain the purpose and scope of this grant"
                      />
                    </Grid>
                  </Grid>
                </Box>

                <Divider />

                {/* Subject Configuration */}
                <Box>
                  <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                    Subject Configuration
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Define who this grant applies to
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FormControl component="fieldset">
                        <FormLabel>Subject Type</FormLabel>
                        <RadioGroup
                          row
                          name="subjectType"
                          value={values.subjectType}
                          onChange={(e) => {
                            handleChange(e);
                            // Reset subjectValue when subjectType changes
                            setFieldValue('subjectValue', '');
                          }}
                        >
                          <FormControlLabel value="ROLE" control={<Radio />} label="Role-Based" />
                          <FormControlLabel value="USER" control={<Radio />} label="User-Specific" />
                        </RadioGroup>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl
                        fullWidth
                        size="small"
                        required
                        error={touched.subjectValue && !!errors.subjectValue}
                      >
                        <InputLabel>
                          {values.subjectType === 'USER' ? 'User' : 'Role'}
                        </InputLabel>
                        <Select
                          name="subjectValue"
                          value={values.subjectValue}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          label={values.subjectType === 'USER' ? 'User' : 'Role'}
                          disabled={values.subjectType === 'USER' && loadingUsers}
                        >
                          {values.subjectType === 'ROLE' ? (
                            // Show GroupRole enum values (filtered by client)
                            getAvailableRoles(values.clientId).map(role => (
                              <MenuItem key={role.value} value={role.value}>
                                {role.label}
                              </MenuItem>
                            ))
                          ) : (
                            // Show users from database
                            users.map(user => (
                              <MenuItem key={user.id} value={user.id}>
                                [{user.client}] {user.lastName}, {user.firstName} ({user.email})
                                {user.groupRole && ` - ${user.groupRole}`}
                              </MenuItem>
                            ))
                          )}
                        </Select>
                        {touched.subjectValue && errors.subjectValue && (
                          <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                            {errors.subjectValue}
                          </Typography>
                        )}
                        {!errors.subjectValue && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.75 }}>
                            {values.subjectType === 'ROLE'
                              ? 'Select the role this grant applies to'
                              : loadingUsers
                                ? 'Loading users...'
                                : `${users.length} active users available`}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                  </Grid>
                </Box>

                <Divider />

                {/* Resource Targeting (v5.002) */}
                <Box>
                  <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                    Resource Targeting
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Select specific resources or leave empty for wildcards. Selections cascade: domain → resource type → resource.
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Domain</InputLabel>
                        <Select
                          name="targetDomain"
                          value={values.targetDomain || ''}
                          onChange={(e) => {
                            const value = e.target.value || null;
                            setFieldValue('targetDomain', value);
                            // Reset dependent fields
                            setFieldValue('targetResourceTypeId', null);
                            setFieldValue('targetResourceId', null);
                            setResources([]);
                          }}
                          label="Domain"
                        >
                          <MenuItem value="">
                            <em>All Domains *</em>
                          </MenuItem>
                          <MenuItem value="OPERATIONS">Operations</MenuItem>
                          <MenuItem value="FINANCE">Finance</MenuItem>
                          <MenuItem value="HUMAN_RESOURCES">Human Resources</MenuItem>
                          <MenuItem value="INFORMATION_TECHNOLOGY">Information Technology</MenuItem>
                          <MenuItem value="MARKETING">Marketing</MenuItem>
                          <MenuItem value="EXECUTIVE">Executive</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Resource Type</InputLabel>
                        <Select
                          name="targetResourceTypeId"
                          value={values.targetResourceTypeId || ''}
                          onChange={(e) => {
                            const value = e.target.value || null;
                            setFieldValue('targetResourceTypeId', value);
                            setFieldValue('targetResourceId', null);
                            if (value) {
                              fetchResourcesByType(value, values.targetDomain);
                            } else {
                              setResources([]);
                            }
                          }}
                          label="Resource Type"
                          disabled={loadingResourceTypes}
                        >
                          <MenuItem value="">
                            <em>All Resource Types *</em>
                          </MenuItem>
                          {resourceTypes
                            .filter(rt =>
                              !values.targetDomain ||
                              !rt.domainRestrictions ||
                              rt.domainRestrictions.length === 0 ||
                              rt.domainRestrictions.includes(values.targetDomain)
                            )
                            .map(rt => (
                              <MenuItem key={rt.id} value={rt.id}>
                                {rt.icon && `${rt.icon} `}{rt.name}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Resource</InputLabel>
                        <Select
                          name="targetResourceId"
                          value={values.targetResourceId || ''}
                          onChange={(e) => {
                            const value = e.target.value || null;
                            setFieldValue('targetResourceId', value);
                          }}
                          label="Resource"
                          disabled={!values.targetResourceTypeId || loadingResources}
                        >
                          <MenuItem value="">
                            <em>All Resources *</em>
                          </MenuItem>
                          {resources.map(resource => (
                            <MenuItem key={resource.id} value={resource.id}>
                              {resource.icon && `${resource.icon} `}{resource.resourceCode} - {resource.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>

                  {/* Wildcard Warning */}
                  {isFullWildcard(values) && (
                    <Alert severity="warning" icon={<WarningIcon />} sx={{ mt: 2 }}>
                      <strong>Full Wildcard Grant:</strong> This grant applies to all domains, resource types, and resources. Use with caution.
                    </Alert>
                  )}
                </Box>

                <Divider />

                {/* Action Configurations (v5.002) */}
                <Box>
                  <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                    Action Configurations
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Configure what actions are allowed or denied, with per-action scope settings.
                  </Typography>

                  {/* ActionConfigs Array */}
                  <Stack spacing={2} sx={{ mt: 2 }}>
                    {values.actionConfigs.map((actionConfig, index) => (
                      <Box
                        key={index}
                        sx={{
                          p: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          bgcolor: 'background.paper'
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography variant="subtitle2" fontWeight="600">
                            Action #{index + 1}
                          </Typography>
                          {values.actionConfigs.length > 1 && (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                const newConfigs = values.actionConfigs.filter((_, i) => i !== index);
                                setFieldValue('actionConfigs', newConfigs);
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>

                        <Grid container spacing={2}>
                          {/* Action and Effect */}
                          <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small" required>
                              <InputLabel>Action</InputLabel>
                              <Select
                                value={actionConfig.action}
                                onChange={(e) => setFieldValue(`actionConfigs[${index}].action`, e.target.value)}
                                label="Action"
                              >
                                <MenuItem value="VIEW">View</MenuItem>
                                <MenuItem value="EDIT">Edit</MenuItem>
                                <MenuItem value="CREATE">Create</MenuItem>
                                <MenuItem value="DELETE">Delete</MenuItem>
                                <MenuItem value="DEACTIVATE">Deactivate</MenuItem>
                                <MenuItem value="EXPORT">Export</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small" required>
                              <InputLabel>Effect</InputLabel>
                              <Select
                                value={actionConfig.effect}
                                onChange={(e) => setFieldValue(`actionConfigs[${index}].effect`, e.target.value)}
                                label="Effect"
                              >
                                <MenuItem value="ALLOW">Allow</MenuItem>
                                <MenuItem value="DENY">Deny</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>

                          {/* ScopeConfig */}
                          <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                              Scope Configuration
                            </Typography>
                            <Stack direction="row" spacing={2} flexWrap="wrap">
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={actionConfig.scopeConfig?.includeChildren || false}
                                    onChange={(e) => setFieldValue(`actionConfigs[${index}].scopeConfig.includeChildren`, e.target.checked)}
                                    size="small"
                                  />
                                }
                                label="Include Children"
                              />
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={actionConfig.scopeConfig?.includePeers || false}
                                    onChange={(e) => setFieldValue(`actionConfigs[${index}].scopeConfig.includePeers`, e.target.checked)}
                                    size="small"
                                  />
                                }
                                label="Include Peers"
                              />
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={actionConfig.scopeConfig?.includeParent || false}
                                    onChange={(e) => setFieldValue(`actionConfigs[${index}].scopeConfig.includeParent`, e.target.checked)}
                                    size="small"
                                  />
                                }
                                label="Include Parent"
                              />
                            </Stack>
                          </Grid>

                          {/* Optional Priority Override */}
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              label="Priority Override (optional)"
                              value={actionConfig.priority || ''}
                              onChange={(e) => setFieldValue(`actionConfigs[${index}].priority`, e.target.value ? parseInt(e.target.value) : null)}
                              helperText="Leave empty to use grant-level priority"
                              inputProps={{ min: 1, max: 999, step: 1 }}
                            />
                          </Grid>
                        </Grid>
                      </Box>
                    ))}

                    {/* Add Action Button - positioned after all actions */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                      <AdminButton
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => {
                          const newActionConfig = {
                            action: 'VIEW',
                            effect: 'ALLOW',
                            scopeConfig: {
                              includeChildren: false,
                              includePeers: false,
                              includeParent: false
                            },
                            priority: null,
                            constraints: null
                          };
                          setFieldValue('actionConfigs', [...values.actionConfigs, newActionConfig]);
                        }}
                      >
                        Add Action
                      </AdminButton>
                    </Box>
                  </Stack>

                  {/* Validation Error for ActionConfigs */}
                  {errors.actionConfigs && typeof errors.actionConfigs === 'string' && (
                    <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                      {errors.actionConfigs}
                    </Typography>
                  )}
                </Box>

                <Divider />

                {/* Grant-Level Settings */}
                <Box>
                  <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                    Grant-Level Settings
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Default priority and status for this grant
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        name="priority"
                        label="Default Priority"
                        value={values.priority}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.priority && !!errors.priority}
                        helperText={touched.priority && errors.priority || '1 (lowest) - 999 (highest). Used when action priority not set.'}
                        inputProps={{ min: 1, max: 999, step: 1 }}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={values.isActive}
                            onChange={(e) => setFieldValue('isActive', e.target.checked)}
                          />
                        }
                        label="Grant is Active"
                        sx={{ mt: 1 }}
                      />
                    </Grid>
                  </Grid>
                </Box>

                {/* Advanced Settings */}
                <Accordion
                  expanded={showAdvanced}
                  onChange={() => setShowAdvanced(!showAdvanced)}
                  sx={{ boxShadow: 'none', border: '1px solid', borderColor: 'divider' }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1" fontWeight="600">
                      Advanced Settings (Optional)
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack spacing={2}>
                      <Typography variant="body2" color="text.secondary">
                        Temporal controls and advanced configuration
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Effective From"
                            type="datetime-local"
                            value={values.effectiveFrom || ''}
                            onChange={(e) => setFieldValue('effectiveFrom', e.target.value || null)}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Effective To"
                            type="datetime-local"
                            value={values.effectiveTo || ''}
                            onChange={(e) => setFieldValue('effectiveTo', e.target.value || null)}
                            InputLabelProps={{ shrink: true }}
                            error={touched.effectiveTo && !!errors.effectiveTo}
                            helperText={touched.effectiveTo && errors.effectiveTo}
                          />
                        </Grid>
                      </Grid>
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              </Stack>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
              <AdminButton
                variant="outlined"
                onClick={onClose}
                disabled={saving}
              >
                Cancel
              </AdminButton>
              <AdminButton
                variant="contained"
                type="submit"
                disabled={saving || isSubmitting}
                startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
              >
                {saving ? 'Saving...' : mode === 'edit' ? 'Update Grant' : 'Create Grant'}
              </AdminButton>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
}
